import React, { useState, useRef, useEffect } from "react";
import { Button, Dialog, DialogTitle, CircularProgress, Typography } from "@material-ui/core";
import { getServerUri } from "../modules/uriUtility";
import AssetUsageInfo from "./AssetUsageInfo";

export function buildDeleteConfirmMessage(assetName, assetNames) {
  let names = [];
  if (Array.isArray(assetNames) && assetNames.length) {
    names = assetNames.filter(Boolean);
  } else if (assetName) {
    names = [assetName];
  }
  const label = names.length ? names.join(", ") : "This asset";
  return (
    <>
      <strong>{label}</strong> will be permanently deleted. are you sure ?
    </>
  );
}

export default function DialogDeleteShell({
  isOpen,
  setIsOpen,
  assetName,
  assetNames,
  assetID,
  assetFile,
  assetType,
  onConfirmDelete,
  createSnackbar,
  errorMessage,
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentLabel: "" });
  const pollIntervalRef = useRef(null);
  const activeProgressIDRef = useRef(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setIsDeleting(false);
      setProgress({ current: 0, total: 0, currentLabel: "" });
    }
  }, [isOpen]);

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!isDeleting) return;
    const sendCancelBeacon = () => {
      const progressID = activeProgressIDRef.current;
      if (!progressID) return;
      const params = new URLSearchParams();
      params.append("accessToken", localStorage.getItem("session.accessToken") || "");
      params.append("progressID", progressID);
      const blob = new Blob([params.toString()], { type: "application/x-www-form-urlencoded" });
      navigator.sendBeacon(getServerUri("version/cancelCascade"), blob);
    };
    window.addEventListener("pagehide", sendCancelBeacon);
    window.addEventListener("beforeunload", sendCancelBeacon);
    return () => {
      window.removeEventListener("pagehide", sendCancelBeacon);
      window.removeEventListener("beforeunload", sendCancelBeacon);
    };
  }, [isDeleting]);

  const handleDelete = () => {
    const progressID = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    activeProgressIDRef.current = progressID;
    setIsDeleting(true);
    setProgress({ current: 0, total: 0, currentLabel: "" });

    try {
      localStorage.setItem("pendingCascade", JSON.stringify({
        assetName: assetName || "",
        assetType: assetType || "",
        startedAt: Date.now(),
      }));
    } catch (e) { /* localStorage disabled */ }

    pollIntervalRef.current = setInterval(() => {
      fetch(getServerUri("version/cascadeProgress"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          progressID,
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data) setProgress(data); })
        .catch(() => { });
    }, 500);

    onConfirmDelete(progressID)
      .then((success) => {
        stopPolling();
        activeProgressIDRef.current = null;
        if (success) {
          try { localStorage.removeItem("pendingCascade"); } catch (e) { /* ignore */ }
          setIsOpen(false);
        } else {
          setIsDeleting(false);
        }
      })
      .catch((err) => {
        stopPolling();
        activeProgressIDRef.current = null;
        setIsDeleting(false);
        console.error(err);
        createSnackbar(errorMessage, "error");
      });
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs" onClose={() => { if (!isDeleting) setIsOpen(false); }}>
      <DialogTitle style={{ fontSize: "1.4rem", padding: "16px 16px 8px" }}>
        {isDeleting ? "Deleting..." : "Delete asset?"}
      </DialogTitle>
      <div style={{ margin: "0 16px 16px" }}>
        {isDeleting ? (
          <div style={{ padding: "8px 0", minHeight: "140px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <CircularProgress size={22} />
              <Typography>Updating affected versions...</Typography>
            </div>
            {progress.total > 0 && (
              <div style={{ marginTop: "12px" }}>
                <Typography
                  style={{
                    fontSize: "13px",
                    color: "#9bcec0",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-all",
                  }}
                >
                  {progress.currentLabel || "Preparing..."}
                </Typography>
                <Typography style={{ fontSize: "12px", color: "#aaa", marginTop: "4px" }}>
                  {progress.current} / {progress.total}
                </Typography>
              </div>
            )}
          </div>
        ) : (
          <>
            <Typography style={{ color: "#ddd", fontSize: "13px", marginBottom: "12px" }}>
              {buildDeleteConfirmMessage(assetName, assetNames)}
            </Typography>
            <AssetUsageInfo assetID={assetID} assetFile={assetFile} assetType={assetType} isOpen={isOpen} />
            <div style={{ marginTop: "12px" }}>
              <Button variant="contained" style={{ backgroundColor: "#CC736E", color: "#fce4ec" }} onClick={handleDelete}>
                DELETE
              </Button>
              <Button variant="contained" style={{ backgroundColor: "#555", color: "#eee", marginLeft: "8px" }} onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
